import {
  InterpolationError,
  hasTemplateDeep,
  interpolateObject,
} from '../interpolation';
import type { ResolvedDataSource } from '../../providers/DataSourceManager.types';
import { ACTION_PREFIX } from '../binding-enhancer/constants';
import type {
  ActionDeclaration,
  ActionParamValue,
} from '../binding-enhancer/types';
import type { ActionDispatcher } from './ActionDispatcher';
import { eventReferencePaths } from './eventReferences';
import type { OutputBindingPolicy } from './hostSlotOutputActions';
import type { FormState } from '@de/providers/DataSourceManager.types';
import type { CeRoute } from '../../providers/RouteProvider';
import { activeProviderRoots } from '../binding-resolver/reservedRoots';
import { markPublishedScopeContext } from '../published-scope/unpublishedScope';
import {
  LEAD_SUBMIT_ACTION_TYPE,
  LEAD_SUBMIT_FORM_PARAM,
} from '../configurable-step/leadSubmitState';
import {
  LEAD_SUBMIT_ROUTING_ROOTS,
  resolveLeadSubmitValues,
} from '../configurable-step/resolveLeadSubmitValues';

/**
 * Runtime context for {@link resolveActions}. `dispatcher` is the
 * registered {@link ActionDispatcher} (see DB-2.1); the remaining
 * fields mirror {@link BindingContext} so `_action.params` templates
 * can reference the same grammar as `_bind.*` and as DSM source
 * `query`/`body` templates: `{ context: flowContext, formState, item,
 * event, route, scope, ...sources }`.
 */
export interface ActionResolverContext {
  readonly dispatcher: ActionDispatcher;
  readonly sources: Readonly<Record<string, ResolvedDataSource>>;
  readonly item?: unknown;
  readonly formState: FormState;
  /**
   * Live formState read, used in place of the render-time `formState` snapshot
   * whenever a declaration's params are resolved. Each action-list entry calls
   * it, so an entry sees a `formState.set` made by an earlier entry on the same
   * click. Absent hosts fall back to `formState`.
   */
  readonly readFormState?: () => FormState;
  readonly flowContext: Readonly<Record<string, unknown>>;
  /** Name of the enclosing `platform.form`; absent outside a form. */
  readonly formName?: string;
  /** Host-injected route, same object `{{route.*}}` reads in bindings. */
  readonly route?: CeRoute;
  /** Nearest published scope, same bag `{{scope.*}}` reads in bindings. */
  readonly scope?: Readonly<Record<string, unknown>>;
  /**
   * Component-self snapshot exposed via the `{{this.<key>}}`
   * interpolation root. Lets `_action.params` reference props of
   * the bound component without the author hard-coding values that
   * already live on the same node — most notably `formKey` for the
   * cascading-select reset pattern (`_action.onSourceChange.params.keys`
   * = `["{{this.formKey}}"]`).
   *
   * Curated, NOT raw props: the binding-enhancer passes only the
   * fields the action grammar is allowed to read (today: `formKey`).
   * Exposing the full prop bag would leak `_bind.*` declarations
   * and resolved values into action params, which is both a
   * security hazard (binding declarations are author-trusted, action
   * payloads are dispatcher-trusted) and a footgun (a bound
   * `options` array landing inside an action param string would
   * stringify to `[object Object]`).
   */
  readonly componentSelf?: Readonly<Record<string, unknown>>;

  /**
   * Runtime-resolved fallbacks for {@link LINKOUT_ACTION_TYPE} params that the
   * DSL cannot express as static literals — the binding-enhancer resolves them
   * from the enclosing result row / tracking context to the SAME values the
   * `ProductClicked` event carries, so the Linkout / HLF conversion is
   * attributed and classified identically (Mobius parity):
   *   - `formatType` / `formatSubtype` — per-card monetization classification
   *     (StudyMatch rows => `dexp-unqualified`, bucketed results =>
   *     `dexp-results`, Editorial Listings => `dexp-ad` /
   *     `dexp-editorial-listings`).
   *   - `correlationId` — the per-click correlation
   *     (`productClickedCorrelationId || formViewedCorrelationId`) so the
   *     server-side `ConversionTracked` / `ConversionTrackedMeta` events join to
   *     the originating `ProductClicked` (mirrors the Voyager POST's top-level
   *     `conversion.correlationId`).
   *
   * Merged into {@link LINKOUT_ACTION_TYPE} params only (see
   * {@link buildHandler}) as a FALLBACK: an explicit, non-empty `_action.params`
   * value always wins. Absent for non-result / non-product nodes.
   */
  readonly linkoutParamFallbacks?: {
    readonly formatType?: string;
    readonly formatSubtype?: string;
    readonly correlationId?: string;
    readonly country?: string;
    readonly state?: string;
  };

  /**
   * Set for a component whose `_action.*` handlers run on a partner module's
   * signal (`platform.hostSlot` outputs). A declaration becomes a handler only
   * if its action type is on the policy's list and every `{{event.*}}` read in
   * its params sits in a top-level param the policy marks payload-safe.
   * Anything else is treated as a malformed declaration. Absent for every
   * other component, where `{{event.*}}` reads the first handler argument
   * without restriction.
   */
  readonly outputPolicy?: OutputBindingPolicy;
}

/**
 * The generic CMS-resolved linkout action. Its conversion needs per-click
 * runtime values (classification + correlation) the DSL cannot author as
 * literals; they are injected from
 * {@link ActionResolverContext.linkoutParamFallbacks} rather than authored, so
 * it is the one action type {@link buildHandler} augments.
 */
const LINKOUT_ACTION_TYPE = 'navigation.linkout';
const ANCHOR_SUPPRESSED_ACTION_TYPES = new Set([
  'modal.open',
  'modal.close',
  'stub.simulate',
]);

/** Linkout params filled from {@link ActionResolverContext.linkoutParamFallbacks}. */
const LINKOUT_FALLBACK_PARAM_KEYS = [
  'formatType',
  'formatSubtype',
  'correlationId',
  'country',
  'state',
] as const;

type EventHandler = (...args: unknown[]) => void;

/**
 * Walk `rawProps` once, build a map from DOM event name
 * (`onClick`, `onChange`, …) to a handler that invokes
 * `dispatcher.dispatch(type, interpolatedParams)`.
 *
 * Supported param grammar (ADR-11 + plan §3.4):
 *   - `{{context.<key>}}`   — reads host-supplied `flowContext` (matches
 *                              DSM source-query grammar)
 *   - `{{formState.<key>}}` — reads DSM-managed form state
 *   - `{{item.<path>}}`     — reads the repeater-scope item
 *   - `{{route.<path>}}`    — reads the host-injected route
 *   - `{{scope.<key>}}`     — reads the nearest published scope
 *   - `{{event.<path>}}`    — reads the first argument passed to the
 *                              resolved handler (common
 *                              `onChange(value)` / `onSubmit(formData)`
 *                              pattern)
 *   - plus every resolved data source by name, mirroring the binding
 *     resolver's root shape
 *
 * Malformed declarations (missing `type`, wrong shape) are skipped
 * with a single warn-log per event. Malformed templates inside
 * `params` surface at dispatch time — the handler becomes a no-op
 * for that invocation but survives so sibling handlers still run.
 */
export function resolveActions(
  rawProps: Readonly<Record<string, unknown>>,
  ctx: ActionResolverContext,
): Record<string, EventHandler> {
  const handlers: Record<string, EventHandler> = {};

  for (const key of Object.keys(rawProps)) {
    if (!key.startsWith(ACTION_PREFIX)) {
      continue;
    }
    const eventName = key.slice(ACTION_PREFIX.length);
    if (eventName.length === 0) {
      continue;
    }

    const actions = toActionList(rawProps[key]);
    if (actions === undefined) {
      warnOnce(
        `[binding-enhancer] Skipping malformed _action.${eventName} — expected { type: string, params?: Record<string, ActionParamValue> } or a non-empty array of them`,
      );
      continue;
    }

    if (
      ctx.outputPolicy !== undefined &&
      !isPermittedOutputBinding(actions, ctx.outputPolicy)
    ) {
      warnOnce(
        `[binding-enhancer] Skipping _action.${eventName} — an output may run only an allowlisted action, and may read the emitted payload only in a payload-safe param`,
      );
      continue;
    }

    handlers[eventName] = buildHandler(actions, ctx, eventName);
  }

  return handlers;
}

function isPermittedOutputBinding(
  actions: readonly ActionDeclaration[],
  policy: OutputBindingPolicy,
): boolean {
  return actions.every((action) => {
    if (!policy.actionTypes.has(action.type)) return false;
    const safe = policy.payloadSafeParams[action.type] ?? [];
    return eventReferencePaths(action.params).every(
      (path) => path.length === 1 && safe.includes(String(path[0])),
    );
  });
}

/** A payload-safe param carries a string, number or boolean, nothing structured. */
function payloadSafeValuesAreScalar(
  type: string,
  params: Record<string, unknown> | undefined,
  policy: OutputBindingPolicy,
): boolean {
  for (const key of policy.payloadSafeParams[type] ?? []) {
    const value = params?.[key];
    if (
      value !== undefined &&
      typeof value !== 'string' &&
      typeof value !== 'number' &&
      typeof value !== 'boolean'
    ) {
      return false;
    }
  }
  return true;
}

function buildHandler(
  actions: readonly ActionDeclaration[],
  ctx: ActionResolverContext,
  eventName: string,
): EventHandler {
  return (...args: unknown[]): void => {
    const firstArg = args.length > 0 ? args[0] : undefined;

    // A Button with href renders as an anchor. Opening or closing an in-page
    // Modal, or starting a local simulation, is the complete interaction, so
    // suppress the anchor's default navigation once per click, just before the
    // first matching entry dispatches.
    const anchorEvent =
      eventName === 'onClick' &&
      actions.some((action) =>
        ANCHOR_SUPPRESSED_ACTION_TYPES.has(action.type),
      ) &&
      isPreventableEvent(firstArg)
        ? firstArg
        : undefined;
    let navigationSuppressed = false;
    const suppressNavigation = (): void => {
      if (anchorEvent && !navigationSuppressed) {
        anchorEvent.preventDefault();
        navigationSuppressed = true;
      }
    };

    for (let index = 0; index < actions.length; index += 1) {
      const action = actions[index];
      if (!runAction(action, ctx, eventName, firstArg, suppressNavigation)) {
        return;
      }
      if (action.type === 'stub.simulate' && index < actions.length - 1) {
        warnOnce(
          `[binding-enhancer] Entries after stub.simulate in _action.${eventName} run immediately, not after the delay. Put the outcome in then. Dropping the rest of the list.`,
        );
        return;
      }
    }
  };
}

/**
 * Resolve and dispatch one list entry. Returns `false` when the entry fails
 * (missing handler or interpolation error), so the caller skips the rest of the
 * list. Params resolve per entry, so each one reads the live formState.
 */
function runAction(
  declaration: ActionDeclaration,
  ctx: ActionResolverContext,
  eventName: string,
  firstArg: unknown,
  beforeDispatch: () => void,
): boolean {
  const dispatcher = ctx.dispatcher;
  const dispatchType =
    ctx.outputPolicy?.dispatchAs?.[declaration.type] ?? declaration.type;

  if (dispatcher.has && dispatcher.has(dispatchType) === false) {
    warnOnce(
      `[binding-enhancer] No handler registered for action "${dispatchType}"; dropping _action.${eventName}`,
    );
    return false;
  }

  let params: Record<string, unknown> | undefined;
  if (
    declaration.params !== undefined ||
    declaration.type === LEAD_SUBMIT_ACTION_TYPE
  ) {
    try {
      params =
        declaration.type === LEAD_SUBMIT_ACTION_TYPE
          ? resolveLeadSubmitParams(declaration.params ?? {}, ctx, firstArg)
          : resolveParams(declaration.params ?? {}, ctx, firstArg);
    } catch (err) {
      if (err instanceof InterpolationError) {
        warnOnce(
          `[binding-enhancer] Interpolation error in _action.${eventName}.params: ${err.message}`,
        );
        return false;
      }
      throw err;
    }
  }

  if (
    ctx.outputPolicy !== undefined &&
    !payloadSafeValuesAreScalar(declaration.type, params, ctx.outputPolicy)
  ) {
    warnOnce(
      `[binding-enhancer] Dropping _action.${eventName}: a payload-safe param of "${declaration.type}" received a value that is not a string, number or boolean`,
    );
    return false;
  }

  if (declaration.type === LINKOUT_ACTION_TYPE && ctx.linkoutParamFallbacks) {
    params = applyLinkoutParamFallbacks(params, ctx.linkoutParamFallbacks);
  }

  if (ANCHOR_SUPPRESSED_ACTION_TYPES.has(declaration.type)) {
    beforeDispatch();
  }
  try {
    dispatcher.dispatch(dispatchType, params);
  } catch (err) {
    // ActionDispatcher contract says handlers never throw, but we
    // defend against misbehaved implementations so a bad handler
    // cannot crash the rendered component.
    // eslint-disable-next-line no-console
    console.error(
      `[binding-enhancer] ActionDispatcher.dispatch("${declaration.type}") threw`,
      err,
    );
  }
  return true;
}

function isPreventableEvent(
  value: unknown,
): value is { preventDefault: () => void } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'preventDefault' in value &&
    typeof value.preventDefault === 'function'
  );
}

/**
 * Merge the runtime-resolved linkout fallbacks into resolved params as a
 * FALLBACK: an explicit, non-empty `_action.params.<key>` always wins (author
 * escape hatch), so this only fills a key the DSL left unset OR interpolated to
 * an empty string (e.g. `"{{item.productClickedCorrelationId}}"` against a row
 * that carries no such id). Returns the original `params` reference untouched
 * when there is nothing to add so the common (non-result) path allocates
 * nothing.
 */
function applyLinkoutParamFallbacks(
  params: Record<string, unknown> | undefined,
  fallbacks: NonNullable<ActionResolverContext['linkoutParamFallbacks']>,
): Record<string, unknown> | undefined {
  let merged: Record<string, unknown> | undefined;
  for (const key of LINKOUT_FALLBACK_PARAM_KEYS) {
    const fallback = fallbacks[key];
    if (fallback === undefined) continue;
    const current = params?.[key];
    if (current !== undefined && current !== '') continue;
    if (!merged) merged = { ...(params ?? {}) };
    merged[key] = fallback;
  }
  return merged ?? params;
}

/**
 * Every value that reaches the lead submit handler skips the generic walk,
 * which would let it read every root, and resolves through the shared lead
 * value resolver instead:
 * - `lead`, `attributes` and `conversionId` read `LEAD_SUBMIT_VALUE_ROOTS`.
 * - `organizationId` and `offeringId` read `LEAD_SUBMIT_ROUTING_ROOTS`.
 * A value that is refused or unresolved is omitted. `onSuccess` and `onError`
 * pass through as authored; the handler resolves each follow-up when it runs.
 * The form scope is set from the click site, never from authored params.
 */
function resolveLeadSubmitParams(
  authored: Readonly<Record<string, unknown>>,
  ctx: ActionResolverContext,
  firstArg: unknown,
): Record<string, unknown> {
  const {
    lead,
    attributes,
    organizationId,
    offeringId,
    conversionId,
    onSuccess,
    onError,
    ...authoredRest
  } = authored;
  const rest: Record<string, unknown> = authoredRest;
  delete rest[LEAD_SUBMIT_FORM_PARAM];
  const params = resolveParams(
    rest as Readonly<Record<string, ActionParamValue>>,
    ctx,
    firstArg,
  );
  const scope = {
    contextParams: ctx.flowContext.params,
    formState: ctx.readFormState?.() ?? ctx.formState,
  };
  Object.assign(
    params,
    resolveLeadSubmitValues(
      { organizationId, offeringId },
      scope,
      LEAD_SUBMIT_ROUTING_ROOTS,
    ),
    resolveLeadSubmitValues({ conversionId }, scope),
  );
  if (lead !== undefined) params.lead = resolveLeadSubmitValues(lead, scope);
  if (attributes !== undefined) {
    params.attributes = resolveLeadSubmitValues(attributes, scope);
  }
  if (onSuccess !== undefined) params.onSuccess = onSuccess;
  if (onError !== undefined) params.onError = onError;
  if (ctx.formName !== undefined) params[LEAD_SUBMIT_FORM_PARAM] = ctx.formName;
  return params;
}

function resolveParams(
  params: Readonly<Record<string, ActionParamValue>>,
  ctx: ActionResolverContext,
  firstArg: unknown,
): Record<string, unknown> {
  if (!hasTemplateDeep(params)) {
    // Returning a shallow clone keeps the dispatcher from receiving a
    // Readonly object it might try to mutate.
    return { ...params };
  }
  const root: Record<string, unknown> = {
    context: ctx.flowContext,
    formState: ctx.readFormState?.() ?? ctx.formState,
    item: ctx.item,
    event: firstArg,
    // `this` is always present (even as an empty object) so authors
    // get a single deterministic interpolation grammar — `{{this.x}}`
    // resolving to undefined-on-missing-key is a known runtime
    // outcome, while the root being absent entirely would surface as
    // an `InterpolationError` that's harder to debug.
    this: ctx.componentSelf ?? {},
  };
  const providerRoots = activeProviderRoots(ctx);
  if (ctx.route !== undefined) {
    root.route = ctx.route;
  }
  if (ctx.scope !== undefined) {
    root.scope = ctx.scope;
    markPublishedScopeContext(root);
  }
  for (const name of Object.keys(ctx.sources)) {
    if (RESERVED_ACTION_ROOTS.has(name) || providerRoots.has(name)) {
      // See the matching comment in `buildInterpolationContext` —
      // collisions are dropped silently here (the binding resolver
      // already warned for the same source) to keep the dispatcher
      // path quiet on hot events.
      continue;
    }
    root[name] = ctx.sources[name]?.data;
  }
  const resolved = interpolateObject(params, root) as Record<string, unknown>;
  return resolved;
}

/** Reserved interpolation roots for `_action.params` — see binding resolver. */
const RESERVED_ACTION_ROOTS: ReadonlySet<string> = new Set([
  'context',
  'formState',
  'item',
  'event',
  'this',
]);

/**
 * Normalize an `_action.<event>` value to an ordered list. A single valid
 * declaration becomes a one-entry list. An array must be non-empty, and every
 * entry must be valid; otherwise the whole declaration is rejected.
 */
function toActionList(
  value: unknown,
): readonly ActionDeclaration[] | undefined {
  if (Array.isArray(value)) {
    return value.length > 0 && value.every(isActionDeclaration)
      ? value
      : undefined;
  }
  return isActionDeclaration(value) ? [value] : undefined;
}

function isActionDeclaration(value: unknown): value is ActionDeclaration {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const candidate = value as { type?: unknown; params?: unknown };
  if (typeof candidate.type !== 'string' || candidate.type.length === 0) {
    return false;
  }
  if (candidate.params === undefined) {
    return true;
  }
  if (
    candidate.params === null ||
    typeof candidate.params !== 'object' ||
    Array.isArray(candidate.params)
  ) {
    return false;
  }
  return true;
}

const warnedMessages = new Set<string>();

function warnOnce(message: string): void {
  if (warnedMessages.has(message)) {
    return;
  }
  warnedMessages.add(message);
  // eslint-disable-next-line no-console
  console.warn(message);
}

/**
 * Test-only reset for the warn-once ledger. Keeps the warning assertions
 * deterministic across Jest suites.
 */
export function __resetResolveActionsWarnings(): void {
  warnedMessages.clear();
}
